import {ALLIANZ_STATUSES} from './allianz/constanst';
import {
  CURRENCIES,
  GUEST_STATUSES,
  MULTI_PAYMENT_STATUSES,
  MULTI_PAYMENT_STEP_STATUSES,
  PAYMENT_PROVIDERS,
  PAYMENTS_SETTINGS_STATUSES,
  RESERVATION_PAYMENT_TYPES,
  SHORT_LINK_INSTANCE,
  SHORT_LINK_TYPE,
  SUMMARY_PAYMENTS_STATUSES,
} from './constants';
import {SECURITY_DEPOSIT_STATUSES} from './deposits';
import {Country, DivisionLevel} from './locations';

type valueof<T> = T[keyof T];
type InitParams = {
  apikey: string;
  reservationId?: string | null;
  propertyId?: string | null;
  isChekinConsumer?: boolean;
  token?: string;
  maxRequestsRetriesCount?: number;
};

type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

type CoreError = {
  code: string;
  message: string;
  status_code: number;
  errors: {code: number; field: string; message: string}[];
};

type SelectOptionType<T = undefined, V = string, L = string> = {
  value: V;
  label: L;
  data?: T;
  onClick?: () => void;
  isDisabled?: boolean;
};

type Document = {
  back_side_scan?: null | string;
  back_side_scan_download?: null | string;
  back_side_scan_key?: string;
  expiration_date?: null | string;
  front_side_scan?: null | string;
  front_side_scan_download?: null | string;
  front_side_scan_key?: string;
  id: string;
  issue_date?: null | string;
  issue_place?: unknown;
  number: string;
  type?: string;
  type_str?: string;
};

type StatusDetails = {
  code: string;
  details: string;
  label: string;
  meaning: string;
};

type Guest = {
  arrived_from?: {
    country?: string;
    division_lvl_1?: string;
    division_lvl_2?: string;
  };
  biomatch_doc: string | null;
  biomatch_passed: boolean;
  biomatch_selfie: string | null;
  document_passed: boolean;
  birth_date: string;
  birth_place?: {
    country?: string;
    city?: string;
    division_level_3?: string;
  };
  residence?: {
    address?: string;
    city?: string;
    country?: string;
    postal_code?: string;
    division_level_2?: string;
    division_level_3?: string;
  };
  residence_country?: Country;
  citizenship: Country;
  created_at: string;
  document: Document;
  documents: Document[];
  email: string;
  full_name: string;
  gender: string;
  id: string;
  ifa_status: string;
  is_use_in_contract: boolean;
  language: string;
  name: string;
  names: {name: string; surname: string};
  nationality: Country;
  next_destination: {
    address?: string;
    city?: string;
    country?: string;
    division_level_1?: string;
    division_level_2?: string;
  };
  origin: string;
  phone: string;
  phone_details: {code: string; number: string};
  purpose_of_stay: string;
  room: unknown;
  second_surname: string;
  signature: string | null;
  guestapp_status: GUEST_STATUSES;
  status_display: string;
  statuses: {
    police: {
      in: StatusDetails;
      out: StatusDetails;
    };
    statistics: {
      in: StatusDetails;
      out: StatusDetails;
    };
    data: StatusDetails;
    arrival: StatusDetails;
  };
  surname: string;
  tax_exemption: string;
  fiscal_code: string;
  full_tourist_tax: number | null;
  reservation_id: string;
  sex?: string;
  has_been_updated?: boolean;
  is_pre_registered?: boolean;
  has_allianz_insurance?: boolean;
};

type ShortGuest = {
  id: string;
  guestapp_status: GUEST_STATUSES;
  gender: 'M' | 'F';
  document_passed: boolean;
  full_name: string;
  is_pre_registered: boolean;
  residence_country: string;
};

enum GROUP_TYPES {
  single = 'S',
  group = 'G',
  family = 'F',
  tourist = 'T',
}

type GuestGroup = {
  id: string;
  known_number_of_guests: number;
  added_number_of_guests: number;
  adults: number;
  children: number;
  seniors: number;
  lead_added_sent_to_mixpanel: boolean;
  all_registered_sent_to_mixpanel: boolean;
  members: ShortGuest[];
  leader_id: string;
  group_type: GROUP_TYPES;
  reservation_id: string;
};

type ShortReservation = {
  guest_group: GuestGroup;
  id: string;
  origin: string;
  source: string | null;
  status: string;
  check_in_date: string;
  check_out_date: string;
  created_at: string;
  updated_at: string;
  housing_id: string;
  auto_signature: boolean;
  guest_group_id: string;
  total_amount_to_pay: string;
  insurance_setup_id: string;
  signup_form_link: string;
  access_end_time?: string;
  access_start_time?: string;
};

type ConfirmReservationDetails = {
  numberOfGuests?: number;
  adults?: number;
  children?: number;
};

type TaxExemption = {
  name: string;
  id: string;
  stat_type: string;
};

type BookingSummary = {
  allianz: BookingAllianz;
  check_in_date: string;
  check_out_date: string;
  expected_guests: number;
  booking: Booking;
  property_name: string;
  guest_registration: BookingGuestRegistration;
  property_protection: ReservationPaymentSummary;
  taxes: ReservationPaymentSummary;
  booking_payments: ReservationPaymentSummary;
  upselling: BookingUpselling;
  country_code: string;
  display_name: string;
  nights_of_stay: number;
  is_remote_access_enabled: boolean;
};

type BookingUpselling = {
  active: boolean;
};

type BookingAllianz = {
  active: boolean;
};

type ReservationPaymentSummary = {
  active: boolean;
  status?: null | SUMMARY_PAYMENTS_STATUSES;
};

type BookingGuestRegistration = {
  status: 'COMPLETE' | 'INCOMPLETE';
  editable: boolean;
};

type Booking = {
  stage: string;
  age_group: AgeGroup[];
};

type AgeGroup = {
  name: string;
  country: string;
  base_age: number;
};

type Housing = {
  id: string;
  location: string;
  police_account_type: string;
  is_contract_enabled?: boolean;
  is_auto_police_registration_enabled?: boolean;
  has_host_protection_insurance?: boolean;
  host_protection_insurance_provider?: boolean;
  is_biometric_match_for_all_enabled?: boolean;
  security_deposit_status: SECURITY_DEPOSIT_STATUSES;
  damage_protection_status: SECURITY_DEPOSIT_STATUSES;
  seasons?: string[];
  allianz_status: ALLIANZ_STATUSES;
  picture?: {
    id: string;
    src?: string;
  };
  smart_lock_init_range_days?: number;
  manager_id: string;
  name: string;
  display_name: string;
};

type HousingLocation = {
  id: string;
  city: string;
  address: string;
  country: {code: string; alpha_3: string; splitted: string[]; name: string};
  details: {
    division_level_1: DivisionLevel;
    division_level_2: DivisionLevel;
    division_level_3: DivisionLevel;
  };
};

type GuestGroupMembers = {
  count: number;
  next?: string;
  previous?: string;
  results: GuestGroupMember[];
};

type GuestGroupMember = {
  id: string;
  full_name: string;
  gender: string;
  detail: string;
  detail_with_statuses: string;
};

type UpdateGuestGroup = {
  adults?: number;
  children?: number;
  seniors?: number;
};

type PaymentsSettings = {
  balance: number;
  company_name: string;
  external_account_id: string;
  iban: string;
  id: string;
  provider: PAYMENT_PROVIDERS;
  status: PAYMENTS_SETTINGS_STATUSES;
  status_details: string;
  swift: string;
  currency: CURRENCIES;
  is_pre_auth_active: boolean;
};

type ReservationPayment<T = Record<string, unknown>> = {
  amount: string;
  amount_with_commission: number;
  app_commission_amount: number;
  guest_commission_amount: number;
  guest_payment_id: null | string;
  id: string;
  managers_profit: number;
  name: string;
  reservation_id: string;
  status: string;
  status_details: string;
  type: RESERVATION_PAYMENT_TYPES;
  metadata: T;
  date_limit_to_pay: string;
  payment_provider_fee?: number;
  upselling_deal_id?: string;
};

type PaymentStep = {
  id: string;
  multi_payment: string;
  name: string;
  index: number;
  type: string;
  status: MULTI_PAYMENT_STEP_STATUSES;
  status_details: string;
  error_code: string;
  metadata: {
    guest_payment_id: string;
    external_client_secret: string;
    external_payment_intent_id: string;
  };
};

type MultiPayment = {
  id: string;
  version: 'V2' | 'V1';
  reservation: string;
  requested_specification: {
    id: string;
    is_ready_to_pay_for_all: boolean;
    requested_reservation_payment_ids: string[];
    is_deposit_payment_requested: boolean;
    is_damage_protection_payment_requested: boolean;
    is_taxes_payment_requested: boolean;
  };
  status: MULTI_PAYMENT_STATUSES;
  status_details: string;
  current_step: PaymentStep;
  steps: PaymentStep[];
};

type WebsocketType<T = unknown> = WebSocket & {
  message: T & {
    event_type: string;
  };
};

type GuestTaxExemption = {
  id: string;
  age: number;
  exemption?: string | null;
  guest_id?: string;
  rule_id?: string;
};

type GuestsTaxes = {
  amount: number;
  calculate_only: boolean;
  guest_list: GuestTaxExemption[];
  id: string;
  reservation_id: string;
  status: 'COMPLETE' | 'PENDING';
};

type SeasonRule = {
  id: string;
  less_than: null | number;
  price: number;
  season_id: string;
  up_to: null | number;
};

type Exemption = {
  exemption_type: string;
  exemption_name: string;
  id: string;
  price: number;
  season_id: string;
};

type SeasonLink = {
  housing_id: string;
  id: string;
  season_id: string;
};

type Season = {
  exemptions: Exemption[];
  price_per_night: number;
  from_date: string;
  housings: string[];
  id: string;
  is_max_nights_taxed: boolean;
  max_nights: number | null;
  max_nights_rate: number | null;
  name: string;
  rules: SeasonRule[];
  season_links: SeasonLink[];
  to_date: string;
};

type Paginated<Result = unknown> = {
  count: number;
  next: null | string;
  previous: null | string;
  results: Result[];
};

type CartPayment = {
  amount: string;
  amount_with_commission: string;
  commission_amount: string;
  id: string;
  name: string;
  obj_type: RESERVATION_PAYMENT_TYPES;
  label_text: string;
  payment_type: 'RETENTION_PAYMENT' | 'ONLINE_PAYMENT' | 'PRE_AUTH_PAYMENT';
  upselling_origin: 'BOOKING' | 'ALLIANZ';
  items_ids: string[];
};
type PaymentsCart = {
  online_payments_total: string;
  pay_later_payments: CartPayment[];
  pay_now_payments: CartPayment[];
  pre_auth_payments_total: string;
  retention_payments_total: string;
  total_amount_to_pay: string;
};

type FetchReservationParams = {
  id?: string;
  externalId?: string | null;
};

type PaymentOrderHistoryItem = {
  id: string;
  obj_type: 'ReservationPayment' | 'SecurityDeposit';
  name: string;
  label_text: string;
  amount: string;
  amount_with_commission: string;
  updated_at_date: string;
};

type PaymentOrderHistory = {
  name: 'Payment details' | 'Pre-authorizations' | 'Retentions' | 'Refunds';
  date: string | null;
  total_amount: string;
  total_amount_with_commission: string;
  items: PaymentOrderHistoryItem[];
};

type OrderHistory = {
  payment_details: PaymentOrderHistory;
  pre_authorizations: PaymentOrderHistory;
  retentions: PaymentOrderHistory;
  refunds: PaymentOrderHistory;
  cards_info: {
    last4: string;
    date: string;
  }[];
};

type ThirdPartyDealPayload = {
  marketplace_item?: string;
  reservation_id?: string;
  housing_id?: string;
  city_code?: string;
  guest_id?: string;
  plans: string[];
  nights_of_stay?: number;
  deal_id?: string;
};

type CreateOrUpdateBulkLockPayload = {
  third_party_deals: string[];
  trigger: string;
  bulk_lock_id?: string;
};

type PrivacyPolicyDocument = {
  id: string;
  title: string;
  language: string;
  document: string;
  document_download: string;
};

type PrivacyPolicySettings = {
  id: string;
  documents: PrivacyPolicyDocument[];
  default_language: string;
  has_custom_privacy_policy: boolean;
};

export enum LOCK_ACCESS_TYPES {
  private = 'PRIVATE',
  common = 'COMMON',
}

type Lock = {
  id: string;
  room_number: string;
  access_type: LOCK_ACCESS_TYPES;
  name: string;
};

type LockDetail = {
  collection_code: string;
  has_expired: boolean;
  id: string;
  lock: Lock;
  pass_code: string;
  unlock_link: string;
  token: string;
};

type LockReservation = {
  housing_instructions: string;
  is_available: boolean;
  is_feature_active: boolean;
  is_show_keys_enabled: boolean;
  unlock_links: LockDetail[];
};

type UnlockResponse = {
  message: string;
  redirect_link: string | null;
  status_code: number;
};

type Contract = {
  file: string;
  id: string;
  status: 'COM';
};

type ShortLink = {
  instance_id: string;
  instance_class: SHORT_LINK_INSTANCE;
  type: SHORT_LINK_TYPE;
  unique_id: string;
};

type OldShortLink = {object_id: string; user: string; type: SHORT_LINK_TYPE};

export type {
  valueof,
  DeepPartial,
  CoreError,
  SelectOptionType,
  InitParams,
  ShortReservation,
  ShortGuest,
  Guest,
  GuestGroup,
  Document,
  TaxExemption,
  ConfirmReservationDetails,
  BookingSummary,
  Housing,
  GuestGroupMembers,
  BookingUpselling,
  ReservationPaymentSummary,
  Booking,
  AgeGroup,
  GuestGroupMember,
  HousingLocation,
  UpdateGuestGroup,
  BookingGuestRegistration,
  PaymentsSettings,
  ReservationPayment,
  MultiPayment,
  WebsocketType,
  GuestsTaxes,
  GuestTaxExemption,
  Season,
  SeasonRule,
  Paginated,
  CartPayment,
  PaymentsCart,
  FetchReservationParams,
  OrderHistory,
  ThirdPartyDealPayload,
  CreateOrUpdateBulkLockPayload,
  PaymentOrderHistory,
  PaymentOrderHistoryItem,
  PrivacyPolicySettings,
  LockDetail,
  LockReservation,
  UnlockResponse,
  Contract,
  ShortLink,
  OldShortLink,
};
